import { unwrap } from "@sagot/api-client";
import { CONSOLE_PATHS, EMAIL_MAX_LENGTH, PRODUCT_NAME } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, Field, Icon, Input, toast } from "@sagot/ui";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useErrorMessage } from "../lib/api-errors";
import { zodResolver } from "../lib/zod-resolver";
import { useApi } from "../session/session-provider";
import { AuthLayout } from "./components/auth-layout";
import { FormAlert } from "./components/form-alert";
import { IconBadge } from "./components/icon-badge";

function useForgotSchema() {
  const { t } = useTranslation();
  return z.object({ email: z.string().trim().min(1, t("auth.common.emailRequired")).max(EMAIL_MAX_LENGTH).email(t("auth.common.emailInvalid")) });
}

function SuccessPanel({ email, onResend }: { email: string; onResend: () => Promise<void> }) {
  const { t } = useTranslation();
  return (
    <section className="animate-rise motion-reduce:animate-none" aria-labelledby="fp-success-title">
      <IconBadge icon="check-circle" tone="success" />
      <h1 id="fp-success-title" className="mt-5 text-2xl font-extrabold tracking-tight">
        {t("auth.forgot.successHeading")}
      </h1>
      <p className="mt-1 text-fg-muted">
        {t("auth.forgot.successLead")} <strong className="break-words text-fg">{email}</strong>, {t("auth.forgot.successTail")}
      </p>
      <div className="mt-6 space-y-2.5">
        <Button asChild size="lg" className="w-full">
          <AppLink href={CONSOLE_PATHS.login}>
            <Icon name="arrow-left" />
            {t("auth.common.backToSignIn")}
          </AppLink>
        </Button>
      </div>
      <div className="mt-6 rounded-lg border border-line bg-surface px-4 py-3 text-xs text-fg-muted">
        <div className="flex items-start gap-2">
          <Icon name="info" className="mt-0.5 size-3.5 text-fg-subtle" />
          <span>
            {t("auth.forgot.noteLead")}{" "}
            <button type="button" className="font-semibold text-primary-ink hover:underline" onClick={() => void onResend()}>
              {t("auth.forgot.sendAgain")}
            </button>
            {t("auth.forgot.noteTail")} <span className="font-semibold text-fg">{t("auth.forgot.team")}</span>.
          </span>
        </div>
      </div>
    </section>
  );
}

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, formState } = useForm<{ email: string }>({ resolver: zodResolver(useForgotSchema()), defaultValues: { email: "" } });

  const send = async (email: string) => {
    await unwrap(api.POST("/v1/auth/forgot", { body: { email } }));
  };
  const onSubmit = handleSubmit(async ({ email }) => {
    setError(null);
    try {
      await send(email);
      setSentTo(email);
    } catch (caught) {
      setError(message(caught));
    }
  });
  const resend = async () => {
    if (!sentTo) return;
    try {
      await send(sentTo);
      toast.success(t("auth.forgot.sentAgain"));
    } catch (caught) {
      toast.error(message(caught));
    }
  };

  return (
    <AuthLayout title={t("auth.forgot.title", { product: PRODUCT_NAME })} back={{ label: t("auth.common.backToSignIn"), href: CONSOLE_PATHS.login }}>
      {sentTo ? (
        <SuccessPanel email={sentTo} onResend={resend} />
      ) : (
        <section aria-labelledby="fp-title">
          <IconBadge icon="key" />
          <h1 id="fp-title" className="mt-5 text-2xl font-extrabold tracking-tight">
            {t("auth.forgot.heading")}
          </h1>
          <p className="mt-1 text-fg-muted">{t("auth.forgot.subtitle", { product: PRODUCT_NAME })}</p>
          {error ? <FormAlert title={error} onDismiss={() => { setError(null); }} className="mt-5" /> : null}
          <form className="mt-6 space-y-4" noValidate onSubmit={(event) => void onSubmit(event)}>
            <Field label={t("auth.common.workEmail")} error={formState.errors.email?.message}>
              {/* eslint-disable-next-line jsx-a11y/no-autofocus -- single-field page; matches the mockup's autofocus */}
              <Input type="email" autoComplete="email" inputMode="email" autoFocus {...register("email")} />
            </Field>
            <Button type="submit" size="lg" className="w-full" loading={formState.isSubmitting}>
              {t("auth.forgot.submit")}
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-fg-muted">
            {t("auth.forgot.remembered")}{" "}
            <AppLink href={CONSOLE_PATHS.login} className="font-semibold text-primary-ink hover:underline">
              {t("auth.common.backToSignIn")}
            </AppLink>
          </p>
        </section>
      )}
    </AuthLayout>
  );
}
